<template>
  <div ref="wrapper" class="tooltip-wrapper" @mouseenter="show" @mouseleave="queueHide(hideDelay)" @wheel="hide">
    <slot name="trigger" />
  </div>

  <Teleport v-if="!disabled" to="body">
    <div
      v-if="visible"
      ref="tooltip"
      class="tooltip-content"
      :style="[positionStyles, tooltipStyle]"
      @mouseenter="hideQueued = false"
      @mouseleave="queueHide(hideDelay)"
    >
      <slot />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
// A newer variant of InfoPopupComponent with fixed positioning that works better with overlapping in modals. Does not stay when clicked

import { waitForTimeout } from '@/utils/tools'
import { ref, nextTick, StyleValue } from 'vue'

const props = withDefaults(
  defineProps<{
    alwaysVisible?: boolean
    tabindex?: string
    disabled?: boolean
    direction?: 'down' | 'up'
    hideDelay?: number
    tooltipStyle?: StyleValue | string
  }>(),
  { direction: 'down' },
)

const visible = ref(!!props.alwaysVisible)
const wrapper = ref<HTMLElement>()
const tooltip = ref<HTMLElement>()
const positionStyles = ref<StyleValue>()

const show = async () => {
  hideQueued = false
  visible.value = true
  await nextTick()

  if (wrapper.value && tooltip.value) {
    const triggerRect = wrapper.value.getBoundingClientRect()
    const tooltipRect = tooltip.value.getBoundingClientRect()

    const triggerLeftPosition = triggerRect.left + triggerRect.width / 2
    const translateX = Math.min(tooltipRect.width / 2, triggerLeftPosition)

    const tooltipStartPosition = { down: triggerRect.bottom, up: triggerRect.top }

    const overflowsTop = tooltipStartPosition[props.direction] - tooltipRect.height < 0
    // const overflowsBottom = tooltipRect.bottom > window.innerHeight

    const directionUp = props.direction === 'up' && !overflowsTop
    const directionDown = props.direction === 'down' || overflowsTop

    positionStyles.value = {
      top: directionDown ? `${tooltipStartPosition.down}px` : undefined,
      bottom: directionUp ? `${window.innerHeight - triggerRect.top}px` : undefined,
      left: `${triggerLeftPosition}px`,
      transform: `translateX(-${translateX}px)`,
    }
  }
}

let hideQueued = false

const queueHide = async (delayMs?: number) => {
  // needed regardless of delay to let the tooltip stay up when the mouse leave and element and enter another element
  hideQueued = true
  await waitForTimeout(delayMs)
  if (hideQueued) {
    hide()
  }
}

const hide = () => {
  visible.value = false
}
</script>

<style scoped>
.tooltip-wrapper {
  display: inline-block;
  padding: 3px 0;
}

.tooltip-content {
  position: fixed;
  z-index: 9999;

  white-space: pre-line;
  font-size: 0.8em;
  font-weight: 100;
  text-wrap: wrap;
  color: var(--text-colour);
  border: 1px solid #ccc;
  border-radius: 2px;
  padding: 0.4em 0.6em;

  background-color: white;
  box-shadow: 1px 2px 10px #0004;

  > * {
    max-width: 320px;
  }
}
</style>
